import { OktaAuth } from "@okta/okta-auth-js";
import { writeStorage } from "@rehooks/local-storage";
import { useQueryClient } from "@tanstack/react-query";
import { authApiErrorToAuthFailure } from "client/src/auth/authApiErrorToAuthFailure";
import { useMarkNewSeenDocumentNoticesAsOld } from "client/src/hooks/document";
import { useSlobQuery, useSlobMutation, compareQueryKey } from "client/src/hooks/query";

import { invalidateConfigQuery, useHubConfiguration } from "client/src/hooks/useConfig";
import { usePostLoginActions } from "client/src/hooks/user";
import * as analytics from "client/src/utils/analytics";
import { useMFAEnrollmentAnalytics } from "client/src/utils/mfaAnalytics";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { RouteData } from "shared/config/routeData";

import type { AuthnTransaction, AuthnTransactionFunction } from "@okta/okta-auth-js";

import type { UseMutateAsyncFunction } from "@tanstack/react-query";
import type { AuthFailure } from "client/src/auth/authApiErrorToAuthFailure";
import type {
  PathBodyToVariables,
  ResponseToMutationReturnType,
  ResponseError,
} from "client/src/hooks/query";
import type { MFAEnrollmentStep } from "client/src/utils/mfaAnalytics";
import type { UserData } from "shared/rbac/rbac";
import type { FactorType } from "shared/types/Okta";

export type AuthState = {
  authUser: UserData | null;
};

export const useGetAuthUserStatus = () =>
  useSlobQuery<
    | { authUser: UserData; isAuthenticated: true }
    | { authUser: null; isAuthenticated: false }
    | null
  >({
    method: "get",
    path: "/api/users/authenticated",
    map: (data) => {
      if (!data?.authUser) {
        return {
          isAuthenticated: false,
          authUser: null,
        };
      }

      const authUser: UserData = {
        ...data.authUser,
        clients:
          data.authUser.clients?.map((client) => ({
            ...client,
            completedSetupAt: client.completedSetupAt ? new Date(client.completedSetupAt) : null,
          })) ?? null,
        termsOfUseDate: data.authUser.termsOfUseDate
          ? new Date(data.authUser.termsOfUseDate)
          : null,
      };

      return {
        isAuthenticated: true,
        authUser,
      };
    },
    options: {
      staleTime: 0,
      gcTime: 0,
      refetchOnWindowFocus: "always",
      refetchOnMount: false,
      refetchOnReconnect: false,
    },
  });

type EnrollOptions = { profile?: { phoneNumber: string; updatePhone: boolean } };

export interface Factor {
  enroll: ((option?: EnrollOptions) => Promise<AuthnTransaction>) | undefined;
  verify: AuthnTransactionFunction | undefined;
  enrollment: string;
  factorType: FactorType;
  provider: string;
  profile?: {
    phoneNumber?: string;
    email?: string;
  };
  status: "NOT_SETUP" | "PENDING_ACTIVATION" | "ACTIVE" | "INACTIVE";
  vendorName: string;
  activation?: {
    qrcode: {
      href: string;
      type: string;
    };
    factorResult: string; // not used but "WAITING" is an known result. Used for pooling
    sharedSecret: string;
  };
}

export type OktaTransactionResponse = {
  status?: LoginWindowOktaStatuses;
  mfaFactorsToEnroll?: Factor[];
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
  resend?: AuthnTransactionFunction;
  transaction?: AuthnTransaction;
  error?: AuthFailure;
};

export type ChallengeResponse = {
  status?: LoginWindowOktaStatuses;
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
  resend?: AuthnTransactionFunction;
  transaction?: AuthnTransaction;
  error?: AuthFailure;
};

type EnrollReponse = {
  status?: LoginWindowOktaStatuses;
  error?: AuthFailure;
  transaction?: AuthnTransaction;
  mfaOptions?: {
    factor?: Factor;
    rememberDeviceLifetimeInMinutes?: number;
  };
};

type WithErrorResponse = {
  status?: LoginWindowOktaStatuses;
  error?: AuthFailure;
};

export const LOCAL_STORAGE_REMEMBER_USER_KEY = "signin-remember-me-user";

type LoginWindowOktaStatuses =
  | "SUCCESS"
  | "MFA_REQUIRED"
  | "MFA_CHALLENGE"
  | "MFA_ENROLL"
  | "MFA_ENROLL_ACTIVATE";

export const useGetOktaAuth = () => {
  const config = useHubConfiguration();

  const { mutateAsync: updatePostLoginAction } = usePostLoginActions();
  const { markNewDocumentNoticesAsOld } = useMarkNewSeenDocumentNoticesAsOld();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<AuthFailure | undefined>(undefined);
  const [shouldRememberUser, setShouldRememberUser] = useState(false);
  const [signinUsername, setSigninUsername] = useState("");

  const {
    start: startEnrollmentAnalytics,
    submit: submitEnrollmentStep,
    fail: failEnrollmentStep,
    complete: completeEnrollmentAnalytics,
    reset: resetEnrollmentAnalytics,
  } = useMFAEnrollmentAnalytics();

  // Every step goes through here: loading starts and the previous error clears,
  // then the step's `{ error }` result (or a thrown error) becomes the hook's error.
  const runStep = useCallback(
    async <T extends { error?: AuthFailure }>(
      step: () => Promise<T>,
      enrollmentStep?: MFAEnrollmentStep,
      factorType?: Factor["factorType"],
    ): Promise<T | { error: AuthFailure }> => {
      setIsLoading(true);
      setError(undefined);
      if (enrollmentStep) submitEnrollmentStep(enrollmentStep, factorType);
      try {
        const result = await step();
        if (result.error && enrollmentStep) failEnrollmentStep(enrollmentStep);
        setError(result.error);
        return result;
      } catch (thrown) {
        if (enrollmentStep) failEnrollmentStep(enrollmentStep, thrown);
        const failure = authApiErrorToAuthFailure(thrown);
        setError(failure);
        return { error: failure };
      } finally {
        setIsLoading(false);
      }
    },
    [submitEnrollmentStep, failEnrollmentStep],
  );

  const oktaAuth = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/consistent-type-assertions -- @todo
      ((window as any).oktaAuth = new OktaAuth({
        clientId: config.REACT_APP_OKTA_CLIENT_ID,
        issuer: `${config.REACT_APP_OKTA_BASE_URL}/oauth2/default`,
        scopes: config.REACT_APP_OKTA_SCOPES,
        pkce: true,
        redirectUri: `${window.location.origin}/login/callback`,
      })),
    [config],
  );

  const onSignIn = useCallback(
    async (sessionToken: string) => {
      // This creates an iframe to Okta authorizeUrl, returning tokens in it's callback.
      // This reduces the need of one hard redirect in current page
      // preventing redirect querystring to be lost in the process
      // The no iframe option would be oktaAuth.token.getWithRedirect()
      const { tokens } = await oktaAuth.token.getWithoutPrompt({ sessionToken });

      try {
        await updatePostLoginAction({
          data: {
            accessToken: tokens.accessToken?.accessToken ?? "",
            refreshToken: tokens.refreshToken?.refreshToken ?? "",
          },
        });
        await markNewDocumentNoticesAsOld();

        writeStorage(LOCAL_STORAGE_REMEMBER_USER_KEY, shouldRememberUser ? signinUsername : "");
      } catch (error) {
        return { error: authApiErrorToAuthFailure(error) };
      }
    },
    [
      markNewDocumentNoticesAsOld,
      oktaAuth.token,
      updatePostLoginAction,
      shouldRememberUser,
      signinUsername,
    ],
  );

  const getValidFactors = (factors: Factor[]) => factors.filter((f) => f.status !== "NOT_SETUP");
  const getSetupFactors = (factors: Factor[]) =>
    factors.filter((f) => f.status === "NOT_SETUP" || f.status === "PENDING_ACTIVATION");

  const handleOktaTransaction = useCallback(
    async (transaction: AuthnTransaction): Promise<OktaTransactionResponse> => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const factors = (transaction.factors as Factor[] | undefined) || [];

      switch (transaction.status) {
        case "SUCCESS": {
          completeEnrollmentAnalytics();
          if (!transaction.sessionToken) {
            return { error: authApiErrorToAuthFailure("Missing sessionToken from response") };
          }
          const res = await onSignIn(transaction.sessionToken);
          return { status: transaction.status, error: res?.error };
        }
        // For Google Authenticator, this is the only step. The user will get the code himself in the app
        case "MFA_REQUIRED": {
          const validFactors = getValidFactors(factors);
          if (!validFactors.length) {
            return { error: authApiErrorToAuthFailure("There's no 2FA factor option configured") };
          }
          if (validFactors.length > 1) {
            return {
              error: authApiErrorToAuthFailure(
                "There's more than one 2FA factor option configured",
              ),
            };
          }
          const factor = validFactors[0];
          const mfaOptions = {
            factor,
            rememberDeviceLifetimeInMinutes: transaction.policy?.allowRememberDevice
              ? transaction.policy.rememberDeviceLifetimeInMinutes
              : undefined,
          };
          return { status: transaction.status, mfaOptions };
        }
        // Extra step for a passcode factor (SMS, call, or email) after the code is requested
        case "MFA_CHALLENGE": {
          if (!transaction.factor) {
            return { error: authApiErrorToAuthFailure("There's no 2FA factor option configured") };
          }
          const mfaOptions = {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            factor: transaction.factor as Factor,
            rememberDeviceLifetimeInMinutes: transaction.policy?.allowRememberDevice
              ? transaction.policy.rememberDeviceLifetimeInMinutes
              : undefined,
          };
          return {
            status: transaction.status,
            mfaOptions,
            transaction,
          };
        }
        // User doesn't have MFA configured yet
        case "MFA_ENROLL": {
          const setupFactors = getSetupFactors(factors);
          if (!setupFactors.length) {
            return { error: authApiErrorToAuthFailure("There's no 2FA factor option configured") };
          }
          startEnrollmentAnalytics("MFA_ENROLL", transaction.user?.id);
          return {
            status: transaction.status,
            mfaFactorsToEnroll: setupFactors,
          };
        }
        // After successfully enrolling, we receive QR code for first setup/activation (with code challenge)
        case "MFA_ENROLL_ACTIVATE": {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          const factor = transaction.factor as Factor | undefined;
          startEnrollmentAnalytics("MFA_ENROLL_ACTIVATE", transaction.user?.id, factor?.factorType);
          return {
            status: transaction.status,
            mfaOptions: { factor },
            transaction,
          };
        }
        case "LOCKED_OUT":
          return {
            error: authApiErrorToAuthFailure(
              "Your account is locked for security reasons. Please, try again in about 60 minutes",
            ),
          };
        case "PASSWORD_EXPIRED":
          return {
            error: authApiErrorToAuthFailure("Your password has expired."),
          };
        default: {
          console.error(`Unknow unhandled process status ${transaction.status}`);
          // https://github.com/okta/okta-auth-js/blob/HEAD/docs/authn.md#transactionstatus
          return {
            error: authApiErrorToAuthFailure(
              `Unknow unhandled process status ${transaction.status}`,
            ),
          };
        }
      }
    },
    [onSignIn, startEnrollmentAnalytics, completeEnrollmentAnalytics],
  );

  /*
    Possible options
    options: {warnBeforePasswordExpired: true, multiOptionalFactorEnroll: false}
  */
  const signIn = useCallback(
    async ({
      username,
      password,
      rememberMe,
    }: {
      username: string;
      password: string;
      rememberMe: boolean;
    }): Promise<OktaTransactionResponse> => {
      setShouldRememberUser(rememberMe);
      setSigninUsername(username);
      resetEnrollmentAnalytics();
      return runStep(async () => {
        const transaction = await oktaAuth.signInWithCredentials({ username, password });
        return await handleOktaTransaction(transaction);
      });
    },
    [oktaAuth, handleOktaTransaction, runStep, resetEnrollmentAnalytics],
  );

  const verifyAuthAppMFA = async (
    passcode: string,
    factor: Factor,
    rememberDevice: boolean,
  ): Promise<WithErrorResponse> =>
    runStep(async () => {
      if (!factor.verify) {
        return { error: authApiErrorToAuthFailure("Inconsistent Factor config (verify)") };
      }
      const factorTransaction = await factor.verify({ passCode: passcode, rememberDevice });
      return await handleOktaTransaction(factorTransaction);
    });

  const verifyPasscode = async (
    passcode: string,
    transaction: AuthnTransaction,
    rememberDevice: boolean,
  ): Promise<WithErrorResponse> =>
    runStep(async () => {
      if (!transaction.verify) {
        return { error: authApiErrorToAuthFailure("Inconsistent Factor config (verify)") };
      }
      const factorTransaction = await transaction.verify({ passCode: passcode, rememberDevice });
      return await handleOktaTransaction(factorTransaction);
    });

  const enrollFactor = async (factor: Factor): Promise<EnrollReponse> =>
    runStep(
      async () => {
        if (!factor.enroll) {
          return {
            error: authApiErrorToAuthFailure("Inconsistent transaction config (factor enroll)"),
          };
        }
        const factorTransaction = await factor.enroll();
        return await handleOktaTransaction(factorTransaction);
      },
      "enroll",
      factor.factorType,
    );

  const enrollMFAPhoneBased = async (factor: Factor, phoneNumber: string): Promise<EnrollReponse> =>
    runStep(
      async () => {
        if (!factor.enroll) {
          return {
            error: authApiErrorToAuthFailure("Inconsistent transaction config (factor enroll)"),
          };
        }
        const factorTransaction = await factor.enroll({
          profile: {
            phoneNumber,
            updatePhone: true,
          },
        });
        return await handleOktaTransaction(factorTransaction);
      },
      "enroll",
      factor.factorType,
    );

  const requestPasscode = async (factor: Factor): Promise<ChallengeResponse> =>
    runStep(async () => {
      if (!factor.verify) {
        return { error: authApiErrorToAuthFailure("Inconsistent Factor config (verify)") };
      }
      const factorTransaction = await factor.verify(); // empty verify sends the passcode
      return await handleOktaTransaction(factorTransaction);
    });

  const resendPasscode = async (
    transaction: AuthnTransaction,
    factorType: Factor["factorType"],
  ): Promise<ChallengeResponse> =>
    runStep(
      async () => {
        if (!transaction.resend) {
          return { error: authApiErrorToAuthFailure("Inconsistent Transaction config (resend)") };
        }
        const factorTransaction = await transaction.resend(factorType);
        return await handleOktaTransaction(factorTransaction);
      },
      "resend",
      factorType,
    );

  const setupMFA = async (
    passcode: string,
    transaction: AuthnTransaction,
  ): Promise<WithErrorResponse> =>
    runStep(async () => {
      if (!transaction.activate) {
        return { error: authApiErrorToAuthFailure("Inconsistent transaction config (activate)") };
      }
      const transactionActivationResponse = await transaction.activate({ passCode: passcode });
      return await handleOktaTransaction(transactionActivationResponse);
    }, "activate");

  const returnToFactorSelection = async (
    transaction: AuthnTransaction,
  ): Promise<OktaTransactionResponse> =>
    runStep(async () => {
      if (!transaction.prev) {
        return { error: authApiErrorToAuthFailure("Inconsistent transaction config (prev)") };
      }
      const previousTransaction = await transaction.prev();
      return await handleOktaTransaction(previousTransaction);
    });

  return {
    oktaAuth,
    oktaStep: {
      signIn,
      verifyAuthAppMFA,
      verifyPasscode,
      enrollFactor,
      enrollMFAPhoneBased,
      requestPasscode,
      resendPasscode,
      setupMFA,
      returnToFactorSelection,
    },
    isLoading,
    error,
  };
};

export type OktaStep = ReturnType<typeof useGetOktaAuth>["oktaStep"];

export type LogoutPostType = UseMutateAsyncFunction<
  ResponseToMutationReturnType<{
    success: boolean;
  }>,
  ResponseError,
  PathBodyToVariables<"/api/users/authenticated/logout", unknown, never>,
  unknown
>;

export const useLogout = () => {
  const queryClient = useQueryClient();

  const navigate = useNavigate();

  return useSlobMutation<unknown, { success: boolean }, `/api/users/authenticated/logout`>({
    method: "post",
    path: `/api/users/authenticated/logout`,
    options: {
      async onSuccess() {
        // eslint-disable-next-line react-compiler/react-compiler
        delete window.client;
        analytics.reset();
        await Promise.all([
          invalidateConfigQuery(queryClient),
          queryClient.invalidateQueries({
            predicate: compareQueryKey(["get", `/api/users/authenticated`]),
          }),
        ]);
        await navigate(RouteData.login.getPathTemplate());
      },
    },
  });
};
